import { PropTypes } from 'typed/imports';
import { PureComponent } from 'typed/components/utils';
import { FormattedNumber as OriginalFormattedNumber } from 'react-intl';
import './FormattedNumber.less';

const NAMED_FORMATS = {},


    SHORT_STRING_SUFFIXES = ['a', 'f', 'p', 'n', 'μ', 'm', '', 'K', 'M', 'G', 'T', 'P', 'E'],
    MIN_SUFFIXED_POWER = -SHORT_STRING_SUFFIXES.indexOf(''),
    MAX_SUFFIXED_POWER = SHORT_STRING_SUFFIXES.length - 1 - SHORT_STRING_SUFFIXES.indexOf('');

interface CommonProps {
    value: number;
    className?: string;
    children?: undefined;
}

interface SpecialFormatProps extends CommonProps, Intl.NumberFormatOptions {
    format: string;
}

interface NamedFormatProps extends CommonProps, Intl.NumberFormatOptions {
    format: keyof typeof NAMED_FORMATS | 'short';
}

interface OptionsProps extends CommonProps, Intl.NumberFormatOptions {
    format?: '';
}

export default class FormattedNumber extends PureComponent<SpecialFormatProps | NamedFormatProps | OptionsProps> {
    static readonly propTypes = {
        value: PropTypes.number.isRequired,
        format: PropTypes.string,
        className: PropTypes.string
    };

    render() {
        if (!this.props.format) {
            return <OriginalFormattedNumber {...this.props} />;
        }
        else if (this.props.format === 'short') {
            const abs = Math.abs(this.props.value);
            let closestPower = Math.min(Math.max(MIN_SUFFIXED_POWER, Math.floor(Math.log10(abs) / 3)), MAX_SUFFIXED_POWER);
            if ((closestPower < 0 || Math.round(abs / 10 ** (closestPower * 3)) >= 1000) && closestPower < MAX_SUFFIXED_POWER) {
                closestPower++;
            }
            return (
                <OriginalFormattedNumber {...this.props} format="" style="decimal" value={Math.sign(this.props.value) * abs / 10 ** (closestPower * 3)} >
                    {value => <span>{value}{this.props.value ? SHORT_STRING_SUFFIXES[closestPower - MIN_SUFFIXED_POWER] : ''}</span>}
                </OriginalFormattedNumber>
            );
        }
        else {
            // shitty and very incomplete implementation of https://docs.microsoft.com/en-us/dotnet/standard/base-types/custom-numeric-format-strings
            // additional thingie: '+' character replaced by actual value sign - "-" for negative, empty for zero, "+" for positive
            const [, prefix = '', sign = '', int = '', , frac = '', mult = '', miffix = '', percentage = '', suffix = ''] = /^(.*?)(\+)?([#0,]*)((\.[#0\.]*)*)(,*)([^%]*)(%?)(.*?)$/.exec(this.props.format || '') || [];
            return (
                int || frac ?
                    <span className={`formatted-number ${this.props.value < 0 ? 'negative ' : this.props.value === 0 ? '' : 'positive '}${this.props.className || ''}`}>
                        <OriginalFormattedNumber {...this.props}
                            value={Math.abs(this.props.value) / (mult ? 1000 ** mult.length : 1) * (percentage ? 100 : 1)}
                            format="" style="decimal"
                            useGrouping={int.includes(',')}
                            minimumIntegerDigits={(/0*$/.exec(int) || [''])[0].length}
                            minimumFractionDigits={(/^0*/.exec(frac.replace(/\./g, '')) || [''])[0].length}
                            maximumFractionDigits={frac.length ? (/.*/.exec(frac.replace(/\./g, '')) || [''])[0].length : undefined} >
                            {(value => <span>{prefix + (this.props.value < 0 ? '\u2212' : !sign || this.props.value === 0 ? '' : '+') + value + miffix + percentage + suffix}</span>) /* proper minus sign, https://www.fileformat.info/info/unicode/char/2212 */}
                        </OriginalFormattedNumber>
                    </span> :
                    <span className={`formatted-number ${this.props.value < 0 ? 'negative ' : this.props.value === 0 ? '' : 'positive '}${this.props.className || ''}`}>{this.props.format}</span>
            );
        }
    }
}
